import type { ReactNode } from 'react';
import { css, cx } from '~/styled-system/css';
import { icon as iconRecipe } from '~/styled-system/recipes/icon';
import { ColorToken } from '~/styled-system/tokens';

export type IconSize = 'small' | 'medium' | 'large' | 'large-inset' | 'xlarge';

export type IconProps = {
  /** Box size token. Defaults to `medium`. */
  size?: IconSize;
  color?: ColorToken;
  className?: string;
};

/**
 * Builds a self-wrapping icon component from a raw SVG element.
 *
 * Source SVGs ship with their own (varying) width/height and aspect ratios.
 * The returned component renders the SVG inside a span that constrains it to a
 * consistent square box via the `icon` recipe and forces a uniform height, so
 * icons line up with text in pills, buttons and list rows. Call sites never
 * wrap icons by hand — they render `<XIcon size="small" />` directly.
 */
export function createIcon(svg: ReactNode, displayName: string) {
  function IconComponent({ size, color, className }: IconProps) {
    return (
      <span
        className={cx(
          iconRecipe({ size }),
          color ? css({ color }) : undefined,
          className
        )}
      >
        {svg}
      </span>
    );
  }
  IconComponent.displayName = displayName;
  return IconComponent;
}
